import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Popover,
  Divider,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FileDownloadOutlined as DownloadIcon,
  FilterAltOutlined as FilterAltOutlinedIcon
} from '@mui/icons-material';
import VehicleListTable from 'src/components/vehicles/VehicleListTable';
import NewVehicle from 'src/components/vehicles/NewVehicle';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import API_URL from 'src/api/apiConstants';
import { BASEAPI } from 'src/api/api';
import moment from 'moment';
import { DATE_FORMAT } from 'src/utils/constants';
import { setErrorInfo } from 'src/redux/modalSlice';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { useHistory, useLocation } from 'react-router-dom';

const Vehicles = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const initialParams = new URLSearchParams(location.search);
  const { allVehicleGroups = [] } = useSelector((state) => state.groups);
  const {
    isAdmin,
    siteFilter,
    siteOptions
  } = useAdminSiteFilter(initialParams.get('site') || 'all');

  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(initialParams.get('search') || '');
  const [isLoading, setLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [appliedSite, setAppliedSite] = useState(initialParams.get('site') || 'all');
  const [appliedGroup, setAppliedGroup] = useState(initialParams.get('group') || 'all');
  const [draftSite, setDraftSite] = useState(siteFilter || 'all');
  const [draftGroup, setDraftGroup] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllVehicleGroupList());
    }
  }, [dispatch, isAdmin]);

  const groupOptions = useMemo(
    () => [{ id: 'all', name: 'All Groups' }, ...allVehicleGroups],
    [allVehicleGroups]
  );

  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  const updateQuery = (patch) => {
    const params = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value && value !== 'all') params.set(key, value);
      else params.delete(key);
    });
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    updateQuery({ search: e.target.value });
  };
  const openFilterPopover = (e) => {
    setDraftSite(appliedSite || 'all');
    setDraftGroup(appliedGroup || 'all');
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleClearFilterDrafts = () => {
    setDraftSite('all');
    setDraftGroup('all');
  };

  const handleApplyFilters = () => {
    setAppliedSite(draftSite || 'all');
    setAppliedGroup(draftGroup || 'all');
    updateQuery({ site: draftSite, group: draftGroup });
    closeFilterPopover();
  };

  const handleExport = () => {
    setShowExportConfirm(false);
    setLoading(true);
    BASEAPI.get(`${API_URL.vehicleList}?export=true`, {
      responseType: 'blob',
      timeout: 20000
    })
      .then((res) => {
        const fileName = `vehicles_${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
      })
      .catch(() => {
        dispatch(setErrorInfo({ errorMsg: 'Error downloading report' }));
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Helmet>
        <title>Vehicles | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* ---------- HEADER (SAME AS SITES) ---------- */}
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                VEHICLES
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Manage vehicles, fuel usage, and assignments.
              </Typography>
            </Box>

            {/* Right */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap', lg: 'nowrap' }}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <TextField
                placeholder="Search vehicles..."
                size="small"
                value={searchText}
                onChange={handleSearch}
                sx={{
                  width: {
                    xs: '100%', sm: 240, md: 260, xl: 320
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterAltOutlinedIcon />}
                    onClick={openFilterPopover}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 40,
                      borderRadius: 2,
                      width: {
                        xs: '100%',
                        sm: 140,
                        md: 160,
                        xl: 180
                      },
                      whiteSpace: 'nowrap',
                      mr: 0
                    }}
                  >
                    Filters
                  </Button>
                  <Popover
                    open={isFilterPopoverOpen}
                    anchorEl={filterAnchorEl}
                    onClose={closeFilterPopover}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left'
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        width: { xs: '92vw', sm: 360 },
                        p: 2,
                        borderRadius: 2
                      }
                    }}
                  >
                    <Stack spacing={1.5}>
                      <FilterSelectInput
                        label="Site"
                        name="site_filter"
                        value={draftSite}
                        data={siteOptions}
                        onInputChange={setDraftSite}
                        sx={{ width: '100%', mr: 0 }}
                      />
                      <FilterSelectInput
                        label="Group"
                        name="group_filter"
                        value={draftGroup}
                        data={groupOptions}
                        onInputChange={setDraftGroup}
                        sx={{ width: '100%', mr: 0 }}
                      />
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilterDrafts}
                        sx={{ textTransform: 'none' }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleApplyFilters}
                        sx={{ textTransform: 'none' }}
                      >
                        Apply
                      </Button>
                    </Stack>
                  </Popover>
                </>
              )}

              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={() => setShowExportConfirm(true)}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {isLoading ? 'Exporting...' : 'Export'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  backgroundColor: theme.palette.primary.main,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Add Vehicle
              </Button>
            </Stack>
          </Stack>

          {/* ---------- CONTENT ---------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <VehicleListTable
              searchText={searchText}
              siteFilter={appliedSite}
              groupFilter={appliedGroup}
            />
          </Box>

          <NewVehicle
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ConfirmationDialog
            open={showExportConfirm}
            info="Are you sure you want to export vehicles report?"
            actionName="Export"
            onClose={() => setShowExportConfirm(false)}
            onSubmit={handleExport}
          />
        </Container>
      </Box>
    </>
  );
};

Vehicles.propTypes = {};

export default Vehicles;
